import { useInterfaceDesign } from "../design/InterfaceDesign";
import { designTokens } from "../design/tokens";
import React, { useCallback } from "react";
import { Box, Paper, Typography } from "@mui/material";
import AnimationIcon from "@mui/icons-material/Animation";
import BoltIcon from "@mui/icons-material/Bolt";
import FunctionsIcon from "@mui/icons-material/Functions";
import DataObjectIcon from "@mui/icons-material/DataObject";
import dico from "../../../util/src/language/dico.js";
import { filterValidSceneAnimationEntries } from "../../../util/src/blueprint/runtime/scene-animations.js";
import type { SceneBehaviorCatalog } from "../display-editor/layout-types";
import type {
  SceneBehaviorRoute,
  SceneCatalogSection,
} from "./SceneBehaviorNavigator.js";

export interface SceneHubCanvasProps {
  catalog: SceneBehaviorCatalog;
  onNavigate: (route: SceneBehaviorRoute) => void;
  onOpenNewTab?: (route: SceneBehaviorRoute) => void;
}

interface HubCardDef {
  section: SceneCatalogSection;
  icon: React.ReactNode;
  countKey:
    | "countAnimations"
    | "countEvents"
    | "countFunctions"
    | "countVariables"
    | "countObjects";
  count: number;
}

const SceneHubCanvasComponent: React.FC<SceneHubCanvasProps> = ({
  catalog,
  onNavigate,
  onOpenNewTab,
}) => {
  const { mode, colorMode } = useInterfaceDesign();
  const d = (dico().behavior as Record<string, unknown>).sceneCatalog as
    | Record<string, unknown>
    | undefined;

  const cards: HubCardDef[] = [
    {
      section: "animations",
      icon: <AnimationIcon sx={{ fontSize: 28, color: "#7c3aed" }} />,
      countKey: "countAnimations",
      count: filterValidSceneAnimationEntries(catalog.animations).length,
    },
    {
      section: "events",
      icon: <BoltIcon sx={{ fontSize: 28, color: "#ea580c" }} />,
      countKey: "countEvents",
      count: catalog.customEvents.length,
    },
    {
      section: "functions",
      icon: <FunctionsIcon sx={{ fontSize: 28, color: "#0284c7" }} />,
      countKey: "countFunctions",
      count: catalog.functions.length,
    },
    {
      section: "variables",
      icon: <DataObjectIcon sx={{ fontSize: 28, color: "#16a34a" }} />,
      countKey: "countVariables",
      count: catalog.variables.length,
    },
    {
      section: "objects",
      icon: <DataObjectIcon sx={{ fontSize: 28, color: "#0891b2" }} />,
      countKey: "countObjects",
      count: catalog.objectTypes?.length ?? 0,
    },
  ];

  const getSectionLabel = useCallback(
    (section: SceneCatalogSection): string => {
      const labels = d as Record<string, string> | undefined;
      switch (section) {
        case "animations":
          return labels?.animations ?? "Animations";
        case "events":
          return labels?.events ?? "Events";
        case "functions":
          return labels?.functions ?? "Functions";
        case "objects":
          return labels?.objects ?? "Objects";
        case "variables":
          return labels?.variables ?? "Variables";
      }
    },
    [d],
  );

  const getCountLabel = useCallback(
    (card: HubCardDef): string => {
      const template = (d as Record<string, string> | undefined)?.[
        card.countKey
      ];
      if (template) {
        return template.replace("{n}", String(card.count));
      }
      return `${card.count}`;
    },
    [d],
  );

  const handleCardClick = useCallback(
    (section: SceneCatalogSection, event: React.MouseEvent) => {
      const route: SceneBehaviorRoute = { level: "list", section };
      if (event.metaKey || event.ctrlKey) {
        onOpenNewTab?.(route);
        return;
      }
      onNavigate(route);
    },
    [onNavigate, onOpenNewTab],
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 2,
        p: 4,
        backgroundColor:
          mode === "modern" ? designTokens[colorMode].background : "#fafafa",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0,1fr)",
            md: "repeat(2,minmax(0,320px))",
          },
          gap: 2,
          my: "auto",
          width: "100%",
          maxWidth: 656,
        }}
      >
        {cards.map((card) => (
          <Paper
            key={card.section}
            elevation={1}
            onClick={(event) => handleCardClick(card.section, event)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onNavigate({ level: "list", section: card.section });
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 2,
              minWidth: 0,
              cursor: "pointer",
              transition: "box-shadow 0.15s, transform 0.15s",
              userSelect: "none",
              "&:hover": {
                boxShadow: 4,
                transform: "translateY(-1px)",
              },
            }}
          >
            {card.icon}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {getSectionLabel(card.section)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getCountLabel(card)}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export const SceneHubCanvas = React.memo(SceneHubCanvasComponent);
